import Promise from 'bluebird';
import _ from 'lodash';
import { debounce } from 'shared/utils';
import {
  getCart,
  addItemToCart,
  addItemsToCart,
  updateCartItem,
  updateCartItems,
  removeCartItem,
  addPromoToCart,
  removeCartPromos,
  syncCart,
  shippingOptions,
  updateShippingOption
} from 'shared/models/cart';
import { getRecommendation } from 'shared/models/api';
import { track } from 'shared/models/analytic';
import { trackInfo, ENGRAVING_SUFFIX } from 'shared/models/product';
import { preserveAppScroll } from 'components/app/actions';
import { withSubtotal, engravingForItem } from 'shared/models/cart';
import { generateRandomString } from 'shared/utils';

const init = {
  products: [],
  engravings: [],
  show: false,
  loading: false,
  promo: null,
  shippingOptions: [],
  shippingOptionsLoading: false,
  selectedShippingOption: {},
  loadingRecommendations: false,
  recommendations: [],
  legal: {}
};

export const ADD_CART = 'ADD_CART';
export const SHOW_CART = 'SHOW_CART';
export const HIDE_CART = 'HIDE_CART';
export const REMOVE_CART = 'REMOVE';
export const UPDATE_QTY_CART = 'UPDATE_QTY_CART';
export const UPDATE_ENGRAVING = 'UPDATE_ENGRAVING';
export const REMOVE_ENGRAVING = 'REMOVE_ENGRAVING';
export const RESTORE_CART = 'RESTORE_CART';
export const RESTORE_CART_ERROR = 'RESTORE_CART_ERROR';
export const SELECT_PROMO = 'SELECT_PROMO';
export const REMOVE_PROMO = 'REMOVE_PROMO';
export const CART_LOADING = 'CART_LOADING';
export const CART_LOADED = 'CART_LOADED';
export const SHIPPING_OPTIONS = 'SHIPPING_OPTIONS';
export const SHIPPING_OPTIONS_LOADING = 'SHIPPING_OPTIONS_LOADING';
export const SHIPPING_OPTION_SELECTED = 'SHIPPING_OPTION_SELECTED';
export const RECOMMENDATIONS_LOADING = 'RECOMMENDATIONS_LOADING';
export const RECOMMENDATIONS_LOADED = 'RECOMMENDATIONS_LOADED';

export default function(state = init, action = {}) {
  let cart_id, sku_id, products, newItem, line_items, engravings, newEngraving;
  switch (action.type) {
    case RESTORE_CART:
      return {
        ...state,
        ...action.cart,
        loading: false
      };
    case RESTORE_CART_ERROR:
      return {
        ...state,
        loading: false
      };
    case ADD_CART:
      newItem = withSubtotal(action.payload.item, action.payload.quantity);
      products = [newItem, ...state.products];
      if (action.payload.engraving) {
        newEngraving = withSubtotal(
          action.payload.engraving,
          action.payload.quantity
        );
      }
      engravings = (newEngraving ? [newEngraving] : []).concat(
        state.engravings
      );
      return { ...state, products, engravings };
    case UPDATE_QTY_CART:
      newItem = withSubtotal(action.payload.item, action.payload.quantity);
      products = state.products.map(product => {
        const isUpdatedProduct = product.cart_id
          ? product.cart_id === newItem.cart_id
          : product.sku_id === newItem.sku_id;
        return isUpdatedProduct ? newItem : product;
      });
      return { ...state, products };
    case UPDATE_ENGRAVING:
      newEngraving = withSubtotal(action.engraving, action.quantity);
      engravings = state.engravings.map(e => {
        return e.cart_id === newEngraving.cart_id ? newEngraving : e;
      });
      return { ...state, engravings };
    case REMOVE_ENGRAVING:
      engravings = state.engravings.filter(
        e => e.cart_id !== action.engraving.cart_id
      );
      return { ...state, engravings };
    case SHOW_CART:
      return { ...state, show: true };
    case HIDE_CART:
      return { ...state, show: false };
    case REMOVE_CART:
      cart_id = action.payload.cart_id;
      sku_id = action.payload.sku_id;
      products = cart_id
        ? state.products.filter(p => p.cart_id !== cart_id)
        : state.products.filter(p => p.sku_id !== sku_id);
      engravings = state.engravings.filter(e => e.master_id !== cart_id);
      return { ...state, products, engravings };
    case SELECT_PROMO:
      return { ...state, promo: action.promo };
    case REMOVE_PROMO:
      line_items = state.line_items.filter(i => i.format !== 'PROMO');
      return { ...state, line_items, promo: null };
    case CART_LOADING:
      return { ...state, loading: true };
    case CART_LOADED:
      return { ...state, loading: false };
    case SHIPPING_OPTIONS_LOADING:
      return { ...state, shippingOptionsLoading: true };
    case SHIPPING_OPTIONS:
      return {
        ...state,
        shippingOptionsLoading: false,
        shippingOptions: action.shippingOptions
      };
    case SHIPPING_OPTION_SELECTED:
      return { ...state, selectedShippingOption: action.option };
    case RECOMMENDATIONS_LOADING:
      return { ...state, loadingRecommendations: true };
    case RECOMMENDATIONS_LOADED:
      return {
        ...state,
        loadingRecommendations: false,
        recommendations: action.recommendations
      };
    default:
      return state;
  }
}

export function restore(roundUp = false) {
  return (dispatch, getState) => {
    if (_.isEmpty(getState().address)) return Promise.resolve();
    if (getState().cart.loading) return Promise.resolve();
    dispatch({ type: CART_LOADING });
    return getCart(roundUp)
      .then(cart => {
        if (cart.fee && cart.fee.shipping_name) {
          dispatch({
            type: SHIPPING_OPTION_SELECTED,
            option: {
              name: cart.fee.shipping_name,
              price: cart.fee.shipping_fee
            }
          });
        }
        return dispatch({ type: RESTORE_CART, cart });
      })
      .catch(function(error) {
        console.log('error restore cart', error); // eslint-disable-line no-console
        return dispatch({ type: RESTORE_CART_ERROR });
      });
  };
}

export function transferCart() {
  return dispatch => {
    dispatch({ type: CART_LOADING });
    return syncCart()
      .then(cart => {
        return dispatch({ type: RESTORE_CART, cart });
      })
      .catch(err => {
        console.log('error syncing cart:', err); // eslint-disable-line no-console
        return dispatch({ type: RESTORE_CART_ERROR });
      });
  };
}

export function clearCart() {
  return dispatch => {
    dispatch({ type: RESTORE_CART, cart: { products: [] } });
  };
}

export function addCart(item, qty = 1, opts = {}) {
  return (dispatch, getState) => {
    const cart = getState().cart;

    const roundUp = getState().roundUp;
    if (roundUp && roundUp.userOrg && roundUp.userOrg.roundUpEnabled)
      opts.roundUpEnabled = roundUp.userOrg.roundUpEnabled;

    const existingItem = findExistingCartItem(cart, item, opts);
    if (existingItem)
      return dispatch(updateQty(existingItem, existingItem.qty + qty, opts));

    const payload = { item, quantity: qty };
    if (opts.engraving) payload.engraving = opts.engraving;

    dispatch({ type: CART_LOADING });

    const shippingOption = cart.selectedShippingOption;
    if (!_.isEmpty(shippingOption)) opts.shipping_name = shippingOption.name;

    return addItemToCart(item.sku_id, qty, opts)
      .then(res => {
        track('Product Added', trackInfo(item));
        if (res && res.products) {
          dispatch({ type: RESTORE_CART, cart: res });
        } else {
          const cart_id = res && res[item.sku_id];
          const newItem = cart_id ? { ...item, cart_id } : item;
          if (cart_id && payload.engraving) {
            payload.engraving.master_id = cart_id;
            payload.engraving.cart_id =
              res && res[`${item.sku_id}${ENGRAVING_SUFFIX}`];
          }
          dispatch({ type: ADD_CART, payload: { ...payload, item: newItem } });
        }
        if (opts.checkout) return dispatch(refreshShippingOptions());
      })
      .finally(() => dispatch({ type: CART_LOADED }));
  };
}

export function addSeveral(items, qty = 1, opts = {}) {
  return (dispatch, getState) => {
    const cart = getState().cart;
    const itemsToUpdate = items
      .map(item => ({
        item,
        existingItem: findExistingCartItem(cart, item, opts)
      }))
      .filter(({ existingItem }) => !!existingItem);
    if (itemsToUpdate.length) dispatch(updateSeveral(itemsToUpdate, qty, opts));

    const newItems = items.filter(
      item => !findExistingCartItem(cart, item, opts)
    );
    const payload = newItems.map(item => ({ sku: item.sku_id, qty }));
    if (!payload.length) return;

    dispatch({ type: CART_LOADING });

    const shippingOption = cart.selectedShippingOption;
    if (!_.isEmpty(shippingOption)) opts.shipping_name = shippingOption.name;

    const roundUp = getState().roundUp;
    if (roundUp && roundUp.userOrg && roundUp.userOrg.roundUpEnabled)
      opts.roundUpEnabled = roundUp.userOrg.roundUpEnabled;

    return addItemsToCart(payload, opts)
      .then(res => {
        newItems.forEach(item =>
          track('Product Added', { ...trackInfo(item), collection: true })
        );
        if (res && res.products) {
          dispatch({ type: RESTORE_CART, cart: res });
        } else {
          newItems.forEach(item => {
            const cart_id = res && res[item.sku_id];
            const newItem = cart_id ? { ...item, cart_id } : item;
            dispatch({
              type: ADD_CART,
              payload: { item: newItem, quantity: qty * newItem.qty }
            });
          });
        }
        if (opts.checkout) return dispatch(refreshShippingOptions());
      })
      .finally(() => dispatch({ type: CART_LOADED }));
  };
}

function updateSeveral(itemsToUpdate, additionalQty, opts = {}) {
  return (dispatch, getState) => {
    dispatch({ type: CART_LOADING });

    const payload = itemsToUpdate.map(({ item, existingItem }) => ({
      sku: existingItem.sku_id,
      qty: existingItem.qty + item.qty * additionalQty
    }));

    itemsToUpdate.forEach(({ item, existingItem }) =>
      dispatch({
        type: UPDATE_QTY_CART,
        payload: {
          item: existingItem,
          quantity: existingItem.qty + item.qty * additionalQty
        }
      })
    );

    const cart = getState().cart;
    const shippingOption = cart.selectedShippingOption;
    if (!_.isEmpty(shippingOption)) opts.shipping_name = shippingOption.name;

    const roundUp = getState().roundUp;
    if (roundUp && roundUp.userOrg && roundUp.userOrg.roundUpEnabled)
      opts.roundUpEnabled = roundUp.userOrg.roundUpEnabled;

    return updateCartItems(payload, opts).then(cart => {
      dispatch({ type: CART_LOADED });
      itemsToUpdate.forEach(({ item, qty }) => {
        track('Product Quantity Updated', {
          ...trackInfo(item, qty),
          collection: true
        });
      });
      if (cart) dispatch({ type: RESTORE_CART, cart });
      if (opts.checkout) return dispatch(refreshShippingOptions());
    });
  };
}

export function showCart() {
  const cart_id = generateRandomString(18);
  return (dispatch, getState) => {
    const cart = getState().cart;
    track('Cart Viewed', {
      cart_id,
      products: cart.products.map((item, idx) => ({
        product_id: item._id,
        sku: item.sku_id,
        category: item.type,
        brand: item.company,
        name: `${item.title}: ${item.subtitle}`,
        price: item.regular_price,
        quantity: item.qty,
        image_url: item.image,
        variant: item.volume,
        url: `https://www.saucey.com${item.url}`,
        position: idx + 1
      }))
    });
    if (cart.show) return;
    dispatch(preserveAppScroll());
    dispatch({ type: SHOW_CART });
  };
}

export function hideCart() {
  return dispatch => {
    dispatch(preserveAppScroll());
    dispatch({ type: HIDE_CART });
  };
}

export function remove(item, opts = {}) {
  return (dispatch, getState) => {
    dispatch({ type: CART_LOADING });
    dispatch({ type: REMOVE_CART, payload: item });
    const shippingOption = getState().cart.selectedShippingOption;
    if (!_.isEmpty(shippingOption)) opts.shipping_name = shippingOption.name;

    const roundUp = getState().roundUp;
    if (roundUp && roundUp.userOrg && roundUp.userOrg.roundUpEnabled)
      opts.roundUpEnabled = roundUp.userOrg.roundUpEnabled;

    return removeCartItem(item, opts)
      .then(cart => {
        track('Product Removed', trackInfo(item));
        if (cart) dispatch({ type: RESTORE_CART, cart });
        if (opts.checkout) return dispatch(refreshShippingOptions());
      })
      .finally(() => dispatch({ type: CART_LOADED }));
  };
}

const debouncedUpdate = debounce((dispatch, item, quantity, opts) => {
  return updateCartItem(item, quantity, opts).then(cart => {
    track('Product Quantity Updated', trackInfo(item, quantity));
    if (cart) dispatch({ type: RESTORE_CART, cart });
    if (opts.checkout) return dispatch(refreshShippingOptions());
  });
}, 500);

export function updateQty(item, quantity, opts = {}) {
  return (dispatch, getState) => {
    dispatch({ type: CART_LOADING });
    const cart = getState().cart;

    const payload = { item, quantity };
    dispatch({ type: UPDATE_QTY_CART, payload });

    const engraving = engravingForItem(cart.engravings, item);
    if (engraving) {
      if (item.cart_id) engraving.master_id = item.cart_id;
      dispatch({ type: UPDATE_ENGRAVING, engraving, quantity });
      opts.engraving = { ...engraving, qty: quantity };
    }

    const shippingOption = cart.selectedShippingOption;
    if (!_.isEmpty(shippingOption)) opts.shipping_name = shippingOption.name;

    const roundUp = getState().roundUp;
    if (roundUp && roundUp.userOrg && roundUp.userOrg.roundUpEnabled)
      opts.roundUpEnabled = roundUp.userOrg.roundUpEnabled;

    return debouncedUpdate(dispatch, item, quantity, opts).finally(() =>
      dispatch({ type: CART_LOADED })
    );
  };
}

export function updateEngraving(item, engraving, opts) {
  return (dispatch, getState) => {
    dispatch({ type: CART_LOADING });

    const quantity = item.qty;
    dispatch({ type: UPDATE_ENGRAVING, engraving, quantity });

    const cart = getState().cart;
    const shippingOption = cart.selectedShippingOption;
    if (!_.isEmpty(shippingOption)) opts.shipping_name = shippingOption.name;

    opts.engraving = engraving;
    return updateCartItem(engraving, quantity, opts).finally(() =>
      dispatch({ type: CART_LOADED })
    );
  };
}

export function removeEngraving(engraving, opts) {
  return (dispatch, getState) => {
    dispatch({ type: CART_LOADING });
    dispatch({ type: REMOVE_ENGRAVING, engraving });
    const shippingOption = getState().cart.selectedShippingOption;
    if (!_.isEmpty(shippingOption)) opts.shipping_name = shippingOption.name;
    return removeCartItem(engraving, opts)
      .then(cart => {
        if (cart) dispatch({ type: RESTORE_CART, cart });
        if (opts.checkout) return dispatch(refreshShippingOptions());
      })
      .finally(() => dispatch({ type: CART_LOADED }));
  };
}

export function selectPromo(item, opts = {}) {
  return dispatch => {
    dispatch({ type: SELECT_PROMO, promo: item });
    if (opts.checkout) dispatch({ type: CART_LOADING });
    return addPromoToCart(item, opts).then(cart => {
      if (cart) dispatch({ type: RESTORE_CART, cart });
    });
  };
}

export function removePromo(opts = {}) {
  return (dispatch, getState) => {
    const { checkout, cart } = getState();
    track('Coupon Removed', {
      order_id: checkout.id,
      coupon: cart.promo.code,
      coupon_name: cart.promo.desc,
      discount: cart.promo.val
    });
    dispatch({ type: REMOVE_PROMO });
    if (opts.checkout) dispatch({ type: CART_LOADING });
    return removeCartPromos(opts).then(cart => {
      if (cart) dispatch({ type: RESTORE_CART, cart });
    });
  };
}

export function refreshShippingOptions() {
  return (dispatch, getState) => {
    if (getState().address.loadingVendor) return;
    if (!getState().address.shipping || !getState().cart.products.length) {
      const options = [];
      dispatch({ type: SHIPPING_OPTIONS, shippingOptions: options });
      return Promise.resolve(options);
    } else {
      dispatch({ type: SHIPPING_OPTIONS_LOADING });
      return shippingOptions().then(result => {
        dispatch({ type: SHIPPING_OPTIONS, shippingOptions: result });
        if (
          result.length &&
          _.isEmpty(getState().cart.selectedShippingOption)
        ) {
          dispatch({ type: SHIPPING_OPTION_SELECTED, option: result[0] });
        }
        return result;
      });
    }
  };
}

export function selectShippingOption(shippingOption, opts = {}) {
  return dispatch => {
    dispatch({ type: CART_LOADING });
    dispatch({ type: SHIPPING_OPTION_SELECTED, option: shippingOption });
    return updateShippingOption(shippingOption, opts)
      .then(res => {
        dispatch({ type: RESTORE_CART, cart: res });
      })
      .catch(() => {
        dispatch({ type: RESTORE_CART_ERROR });
      });
  };
}

export function getRecommendations(skus) {
  return dispatch => {
    dispatch({ type: RECOMMENDATIONS_LOADING });
    return getRecommendation(skus).then(recommendations => {
      dispatch({ type: RECOMMENDATIONS_LOADED, recommendations });
    });
  };
}

function findExistingCartItem(cart, item, opts) {
  const existingItems = cart.products.filter(
    product => product.sku_id === item.sku_id
  );
  if (!existingItems.length) return null;
  const newEngraving = opts.engraving || {};
  return _.find(existingItems, existingItem => {
    const existingEngraving =
      engravingForItem(cart.engravings, existingItem) || {};
    return (
      existingEngraving.line_1 === newEngraving.line_1 &&
      existingEngraving.line_2 === newEngraving.line_2 &&
      existingEngraving.line_3 === newEngraving.line_3
    );
  });
}
